import { useRef, useState, type RefObject } from 'react';
import { Keyboard, TextInput } from 'react-native';
import { Colors, Flex, PrimaryButton, Typography } from '@banhala/ui-kit';
import { bridge, delay, isWeb } from 'shared/src/utils';
import { isAndroid } from 'shared/src/utils/isAndroid';
import { useAsyncEffect } from 'shared/src/utils/useAsyncEffect';

interface FolderNameEditorProps {
  initialValue: string;
  validate: (value: string) => boolean;
  onSubmit: (value: string) => Promise<void>;
  onClose: () => void;
}

export const FolderNameEditor = ({
  initialValue,
  validate,
  onSubmit,
  onClose,
}: FolderNameEditorProps) => {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<TextInput>(null);
  useKeyboardFocus(ref);

  const handleSubmit = async () => {
    Keyboard.dismiss();
    try {
      await onSubmit(value);
    } catch (error) {
      onClose();
      await delay(500);
      bridge.toast(
        error instanceof Error && error.message
          ? error.message
          : '오류가 발생했어요',
      );
    }
  };

  return (
    <>
      <Flex padding={{ horizontal: 16, vertical: 12 }}>
        <Flex
          borderWidth={1}
          borderColor={'border_blue_primary'}
          borderRadius={'small'}
          padding={{ horizontal: 12, vertical: 15 }}
        >
          <TextInput
            ref={ref}
            defaultValue={initialValue}
            style={{
              fontSize: 15,
              color: Colors.content_primary,
              padding: 0,
              outlineWidth: 0,
            }}
            placeholder="서랍 이름은 최소 한 글자 이상 입력해주세요"
            placeholderTextColor={Colors.content_tertiary}
            autoFocus={!isWeb}
            multiline
            onChangeText={setValue}
            maxLength={30}
          />
          <Flex.Row justifyContent={'flex-end'} padding={{ top: 16 }}>
            <Typography type={'body3'} color={'content_primary'}>
              {value.length}
            </Typography>
            <Typography type={'body3'} color={'content_tertiary'}>
              {' / 30'}
            </Typography>
          </Flex.Row>
        </Flex>
      </Flex>
      <Flex padding={{ horizontal: 12, vertical: 12 }}>
        <PrimaryButton
          variant={'fill'}
          color={'pink'}
          size={'xlarge'}
          state={validate(value) ? undefined : 'disabled'}
          onPress={handleSubmit}
        >
          완료
        </PrimaryButton>
      </Flex>
    </>
  );
};

/**
 * - 바텀시트가 올라올 때 키보드가 보이게 하기 위한 훅
 * - 안드로이드에서는 onFocus가 동작하지 않아 추가
 */
const useKeyboardFocus = (ref: RefObject<TextInput | null>) => {
  useAsyncEffect(async () => {
    if (!isAndroid) {
      return;
    }

    await delay(100);
    ref.current?.blur();
    ref.current?.focus();
  }, []);
};
